import { getLogger } from './logger';
import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
import Cookies from 'js-cookie';
import { getTopLevelDomain } from '@snapchat/parse-domain';

const log = getLogger('clientId');
let clientId: ClientId | null = null; // cache clientId in memory

/**
 * Gets the current client ID. Client ID is a UUID which persists across sessions, uniquely
 * identifying this browser. Client IDs reset every 30 days based on P&L requirements.
 *
 * Note that the reset period is set in two places. One is expiration days in Cookies, the other one
 * is the expiresAfterMs clientId is tracking. Both are 30 days by default.
 *
 * If there was no existing client ID, this function generates one and persists it in local storage.
 * If one already existed, it will be returned unless it is over 30 days old (in which case a new
 * one is generated).
 *
 * Based on the Android reference implementation:
 * https://github.sc-corp.net/Snapchat/android/blob/339929f8e21e6340633c940851da30095a5d606b/snapchat/platform/analytics/blizzard/lib/src/main/java/com/snap/blizzard/v2/innards/ClientIdProviderV2Impl.kt
 */
export function getClientId(): string {
  // Load client ID if it hasn't previously been loaded
  if (clientId === null) {
    const str = cookiesEnabled ? Cookies.get(cookieName) : undefined;
    clientId = str ? ClientId.parse(str) : null;
    if (clientId !== null) {
      log.debug('Loaded clientId', clientId);
    }
  }

  // If it has expired, failed to load, or never existed, generate a new one
  if (clientId?.isExpired() ?? true) {
    renewAndSaveClientId();
  }

  if (clientId === null) {
    throw new Error('Unexpected null clientId');
  }
  return clientId.id;
}

/** Renews the client ID and saves it to the blizzard cookie. */
function renewAndSaveClientId() {
  log.info('Generating new clientId');
  clientId = ClientId.generate();
  if (cookiesEnabled) {
    Cookies.set(cookieName, clientId.serialize(), {
      expires: cookiesExpireDays,
      domain: cookiesDomain,
    });
    log.info('clientId is set in Cookies');
  }
}

/** @internal */
class ClientId {
  id: string; // uuid
  timestamp: number; // epoch

  private constructor(data: Pick<ClientId, 'id' | 'timestamp'>) {
    this.id = data.id;
    this.timestamp = data.timestamp;
  }

  /** @returns A new client ID with a random UUID and the current time */
  static generate(): ClientId {
    return new ClientId({
      id: uuidv4(),
      timestamp: new Date().getTime(),
    });
  }

  /**
   * Parses an exiting, serialized, client ID of the format <client ID>:<timestamp>.
   *
   * @param serialized Serialized client ID
   * @returns The parsed object (even if expired), or null if the serialized string was invalid
   */
  static parse(serialized: string): ClientId | null {
    const ar = serialized.split(':');

    if (ar.length === 1) {
      // don't break on migration from the other format without the timestamp
      // actual expiration is handled by the cookie so the time here is irrelevant.
      ar.push(`${new Date().getTime()}`);
    }

    if (ar.length !== 2) {
      return null;
    }

    const data = {
      id: ar[0],
      timestamp: Number.parseInt(ar[1], 10),
    };

    if (!uuidValidate(data.id)) {
      return null;
    }
    if (Number.isNaN(data.timestamp)) {
      return null;
    }

    return new ClientId(data);
  }

  /** @returns True if this client ID has expired */
  isExpired(): boolean {
    // expired if current date is greater than the timestamp date + 30 days or if the timestamp is in the future
    const now = new Date();
    return now > new Date(this.timestamp + expiresAfterMs) || now < new Date(this.timestamp);
  }

  /** @returns The serialized version of this client ID in the format <client ID>:<timestamp> */
  serialize(): string {
    return `${this.id}:${this.timestamp}`;
  }
}

export let cookiesEnabled = true;
let cookiesDomain: string =
  typeof self !== 'undefined' ? getTopLevelDomain(self.location.hostname) : '.snapchat.com';
let cookiesExpireDays = 30;
/**
 * Sets whether cookies are enabled. This is useful for privacy purposes.
 *
 * @internal
 */
export function setCookiesEnabled(
  enabled: boolean,
  domainName = '.snapchat.com',
  expireDays = 30
): void {
  cookiesEnabled = enabled;
  cookiesDomain = domainName;
  cookiesExpireDays = expireDays;
}

const cookieName = 'blizzard_client_id';
const expiresAfterMs = 30 * 24 * 60 * 60 * 1000;
